/**
 * UsersFeedbackPage — admin users-feedback list.
 *
 * Web port of Flutter's `UsersFeedbackScreen`
 * (`lib/presentation/screens/feedback/users_feedback_screen.dart:101-153`):
 *   - Scaffold + app bar with a centered "Feedback" title and a back leading
 *     (`users_feedback_screen.dart:105-120`). Web parity: `FlexibleHeaderList`
 *     `appBarOptions` with `title` + `onLeadingTap` → `Navigator.goBack()`.
 *   - `ListView.builder` of `InAppFeedbackListItem` rows with 16px outer padding
 *     and 8px vertical per-row spacing, plus a trailing loading indicator while
 *     `_hasMore && _isFetching` (`users_feedback_screen.dart:121-151`). Web
 *     parity: `FlexibleHeaderList` in LIST mode (no `gridProps`) — the trailing
 *     load-more indicator is rendered by the list when `hasMore && isLoadingMore`.
 *   - first load on init (`users_feedback_screen.dart:34-55` →
 *     `_onBuildCompleted` → `_getData()`); scroll-to-bottom paginates
 *     (`users_feedback_screen.dart:57-64`). Web parity: `useBasePage({ onInit })`
 *     calls `init()`; the list's `onLoadMore` edge callback calls `loadMore()`.
 *   - row tap → feedback details (`_itemTapped`, `users_feedback_screen.dart:66-68`);
 *     avatar tap → user details (`_userTapped`, `users_feedback_screen.dart:70-72`).
 *
 * Architecture: Page -> useUsersFeedback Hook -> UseCase -> Service.
 *
 * Page owns the screen-key mint + `useBasePage` lifecycle (presentation.md →
 * Page lifecycle); the hook owns all state. Navigation only via `Navigator`.
 *
 * Web parity reference: `SavedUsersPage.tsx` (page-owns-screenKey +
 * `useBasePage({ onInit })` + `FlexibleHeaderList`).
 */

import { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { ContentGridItemState } from '@/presentation/components/common/content/ContentGridItemState';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { InAppFeedbackListItem } from './components/InAppFeedbackListItem';
import { useUsersFeedback } from './hooks/useUsersFeedback';
import type { InAppFeedbackEntity } from '@/domain/entities/feedback/inAppFeedbackEntity';

export const UsersFeedbackPage = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  const { items, isLoading, isLoadingMore, hasMore, error, init, loadMore } =
    useUsersFeedback();

  useBasePage({
    screenKey,
    onInit: () => {
      void init();
    },
    cleanReactQueryBehaviour: 'onPop',
  });

  const handleBack = useCallback(() => {
    Navigator.goBack();
  }, []);

  // Row tap → feedback details, passing the whole entity via `location.state`
  // (mirrors Flutter `_itemTapped` passing the full `inAppFeedback`).
  const handleItemTap = useCallback((feedback: InAppFeedbackEntity) => {
    Navigator.navigateToUsersFeedbackDetails(feedback);
  }, []);

  // Avatar tap → user details (mirrors Flutter `_userTapped`).
  const handleUserTap = useCallback((userId: string) => {
    Navigator.navigateToUserDetails(userId);
  }, []);

  const renderItem = useCallback(
    (feedback: InAppFeedbackEntity) => (
      // 16px horizontal outer padding + 8px vertical per-row spacing mirror
      // Flutter's `ListView` padding (16) and per-item `EdgeInsets.symmetric(
      // vertical: 8)` (users_feedback_screen.dart:129/142-143).
      <div
        style={{
          paddingTop: responsive.spacing.sm,
          paddingBottom: responsive.spacing.sm,
          paddingLeft: responsive.spacing.lg,
          paddingRight: responsive.spacing.lg,
        }}
      >
        <InAppFeedbackListItem
          feedback={feedback}
          onTap={handleItemTap}
          onUserTap={handleUserTap}
        />
      </div>
    ),
    [handleItemTap, handleUserTap, responsive],
  );

  return (
    <FlexibleHeaderList
      qa={{ id: 'users-feedback', status: isLoading ? 'loading' : 'ready' }}
      listQa={{ id: 'users-feedback-list' }}
      items={items}
      renderItem={renderItem}
      getItemKey={(feedback) => feedback.id}
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      emptyListComponent={
        <ContentGridItemState
          isLoading={isLoading}
          errorText={error ? l10n.feedback.errorLoading : undefined}
          emptyText={l10n.feedback.empty}
        />
      }
      backgroundColor={theme.background}
      appBarOptions={{
        title: l10n.feedback.title,
        onLeadingTap: handleBack,
        backgroundColor: theme.background,
      }}
    />
  );
};
